/* eslint-disable prettier/prettier */
/** @jsxImportSource @emotion/react */
import { useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCityDropdown } from 'cart-ui-react/src/autoshipState/selectors';
import { setCartShipGeoCode } from 'cart-ui-react/src/autoshipState/autoshipSlice';
import { Field, useField } from 'react-final-form';
import * as validations from './validations';
import './cartV2Select.css';

/**
 * If the 'initialValues' for the form do not include a key for a
 * given field, the value will be undefined, then it will have a
 * value after redux is hydrated with data. Going from undefined to
 * defined causes errors, so we want to return an empty string instead.
 */
const defaultFormat = (v) => {
  return v ?? '';
};

interface Props {
  label: string;
  name: string;
  options: {
    option: string | number;
    value: string | number;
    geoCode: string | number;
  }[];
  css?: React.CSSProperties;
  required?: boolean;
  validate?: (inputValue: string | number) => any;
  format?: (inputValue: string | number) => any;
}

/**
 *
 * @param label the label for the input
 * @param name name of the input field (also the key for the form values object)
 * @param options array of options for select. Expected format: { label, value }
 * @param css (optional) css override
 * @param required (optional) whether or not the field should be required with validation errors
 * @param validate (optional) validation function to run the input through
 * @param format (optional) format the returned value of the field
 */
const Select = forwardRef(
  (
    {
      label,
      name,
      options,
      css = null,
      required = false,
      validate = null,
      format,
    }: Props,
    ref
  ) => {
    const { input: upperInput } = useField(name);
    const selectRef = useRef(null);
    const cityDropdown = useSelector(selectCityDropdown);
    const dispatch = useDispatch();

    useEffect(() => {
      const selectedCity = selectRef.current.selectedIndex;
      if (cityDropdown.length) {
        dispatch(setCartShipGeoCode(cityDropdown[selectedCity][2]));
      }
    }, [selectRef?.current?.selectedIndex, cityDropdown]);

    useImperativeHandle(
      ref,
      () => ({
        setSelected: (value: any) => upperInput.onChange(value.split('|')[0].trim()),
        value: upperInput.value.split('|')[0].trim(),
      }),
      [upperInput]
    );

    const selectStyles = (meta) => {
      return {
        height: '27px',
        border: '1px solid #bbb',
        borderRadius: '2px',
        width: '100%',
        ...(meta.error && meta.touched
          ? {
              outline: '1px solid #f15d22',
            }
          : {}),
      };
    };

    return (
      <Field
        name={name}
        component='select'
        type='text'
        css={{ margin: '8px auto', width: `-webkit-fill-available` }}
        // if field required but no validate function passed, use default
        validate={required && !validate ? validations.required : validate}
        format={format || defaultFormat}
      >
        {({ input, meta }) => {
          return (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                width: '58%',
                ...(css ? { ...css } : {}),
              }}
            >
              <div
                css={{
                  fontSize: '15px',
                }}
              >
                <p style={{ marginBottom: '6px' }}>{label}</p>
              </div>

              <select
                {...input}
                ref={selectRef}
                className='custom-dropdown'
                css={selectStyles(meta)}
              >
                {options.length ? (
                  options.map(({ option, value, geoCode }, index) => (
                    <option key={`${option} ${index}`} value={value + '|' + geoCode}>
                      {option}
                    </option>
                  ))
                ) : (
                  <option />
                )}
              </select>
              {meta.error && meta.touched ? (
                <p
                  css={{
                    color: '#f15d22',
                    fontSize: '12px',
                    lineHeight: '12px',
                    margin: '6px 2px',
                  }}
                >
                  {meta.error}
                </p>
              ) : null}
            </div>
          );
        }}
      </Field>
    );
  }
);

export default Select;
